import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';

dayjs.extend(relativeTime);

const THIS_WEEK_FORMAT = 'dddd';
const OLDER_FORMAT = 'MMM D';

interface FormatShortRelativeDateOptions {
  nowLabel?: string;
  /**
   * Formats the minute/hour duration into a localized unit string, e.g.
   * (count) => `${count}m` (en) or `${count}min` (es). Defaults to the bare
   * English abbreviations 'm'/'h'.
   */
  formatMinutes?: (count: number) => string;
  formatHours?: (count: number) => string;
  /**
   * Wraps the duration forms in a localized "ago" label, e.g.
   * (time) => `${time} ago` (en) or `hace ${time}` (es). Receives the already
   * unit-formatted duration and returns the full string, so callers control
   * word order per locale. Day names, absolute dates, and the now label are
   * never passed through this — those forms don't read naturally with "ago".
   */
  formatAgo?: (time: string) => string;
}

export const formatShortRelativeDate = (
  input: Date | string | null | undefined,
  { nowLabel, formatMinutes, formatHours, formatAgo }: FormatShortRelativeDateOptions = {},
): string => {
  if (!input) return '';
  const when = dayjs(input);
  if (!when.isValid()) return '';

  const minutes = formatMinutes ?? ((count) => `${count}m`);
  const hours = formatHours ?? ((count) => `${count}h`);
  const withAgo = (time: string) => (formatAgo ? formatAgo(time) : time);

  const now = dayjs();
  const diffMinutes = now.diff(when, 'minute');
  if (diffMinutes < 1) return nowLabel ?? 'now';
  if (diffMinutes < 60) return withAgo(minutes(diffMinutes));
  const diffHours = now.diff(when, 'hour');
  if (diffHours < 24) return withAgo(hours(diffHours));
  const diffDays = now.diff(when, 'day');
  if (diffDays < 7) return when.format(THIS_WEEK_FORMAT);
  return when.format(OLDER_FORMAT);
};
